import { useEffect, useRef, useState } from 'react';
import { BackBar, AIOutput, WhatsAppBtn, streamCoach } from './healthShared';

const SUGGESTIONS = [
  'What should I eat tonight?',
  'Give me a 10-minute workout',
  'How can I improve my sleep?',
  'Make me a 7-day Indian meal plan',
  'I have night duty tomorrow. Help me plan.',
];

export default function HealthCoach({ onBack }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [running, setRunning] = useState(false);
  const abortRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || running) return;
    setInput('');
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setRunning(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamCoach({
        message: q,
        context: 'coach',
        history,
        signal: ctrl.signal,
        onChunk: (t) => setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + t };
          return copy;
        }),
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', content: 'Something went wrong. Please try again.' };
          return copy;
        });
      }
    }
    setRunning(false);
  };

  return (
    <div data-testid="health-coach-section" className="flex flex-col" style={{ minHeight: '70vh' }}>
      <BackBar title="AI Health Coach" onBack={onBack} />

      <div className="flex-1 space-y-4 mb-4">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <p className="text-3xl mb-2">🤖</p>
            <p className="font-accent text-xl text-[#273127] mb-1">Ask Gynovate Health</p>
            <p className="text-xs text-[#9AA594] mb-5">Wellness guidance only — no diagnosis, no prescriptions.</p>
            <div className="flex flex-col gap-2 max-w-sm mx-auto">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid={`coach-suggestion-${SUGGESTIONS.indexOf(s)}`} className="px-4 py-3 rounded-2xl bg-white border border-[#E3E5D8] text-sm font-semibold text-[#44513F] hover:border-[#7FA183] hover:bg-[#EDF2EB] transition-colors text-left">
                  “{s}”
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            {m.role === 'user' ? (
              <div className="max-w-[85%] px-4 py-3 rounded-3xl rounded-br-lg bg-[#2E4A3B] text-white text-sm font-medium" data-testid="coach-user-msg">{m.content}</div>
            ) : (
              <div className="max-w-[92%] px-4 py-3 rounded-3xl rounded-bl-lg bg-white border border-[#E8EADE] shadow-sm" data-testid="coach-ai-msg">
                {m.content ? (
                  <>
                    <AIOutput text={m.content} testId={`coach-output-${i}`} />
                    {(!running || i < messages.length - 1) && (
                      <div className="mt-2"><WhatsAppBtn text={m.content} testId={`coach-whatsapp-btn-${i}`} label="WhatsApp" /></div>
                    )}
                  </>
                ) : <p className="text-sm text-[#9AA594] animate-pulse">Thinking…</p>}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="sticky bottom-3 bg-white rounded-full border border-[#E3E5D8] shadow-lg p-1.5 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Ask about food, workouts, sleep…"
          data-testid="coach-input"
          className="flex-1 px-4 py-2.5 rounded-full bg-transparent text-sm text-[#273127] focus:outline-none"
        />
        {running ? (
          <button onClick={() => abortRef.current?.abort()} data-testid="coach-stop-btn" className="px-5 py-2.5 rounded-full bg-[#A85A3E] text-white text-sm font-bold">Stop</button>
        ) : (
          <button onClick={() => send()} data-testid="coach-send-btn" className="px-5 py-2.5 rounded-full bg-[#2E4A3B] text-white text-sm font-bold hover:bg-[#3a6049] transition-colors">Ask</button>
        )}
      </div>
    </div>
  );
}
